import { ResizeObserver } from '@juggle/resize-observer';
import { type RefObject, useEffect, useRef } from 'react';
import { type Options as MeasureOptions, default as originalUseMeasure } from 'react-use-measure';

export function useClickOutside(ref: RefObject<HTMLElement>, callback: () => void): void {
  const handleClick = (e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) {
      callback();
    }
  };
  useEffect(() => {
    document.addEventListener('click', handleClick);
    return () => {
      document.removeEventListener('click', handleClick);
    };
  });
}

export function useInterval(callback: (arg0: number) => void, delay = 0): void {
  const savedCallback = useRef<((arg0: number) => void) | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isCancelled = false;

    // Function to be executed on each animation frame
    function onFrame() {
      if (isCancelled) return;
      try {
        const callback = savedCallback.current;
        if (callback != null) {
          callback(Date.now() - start);
        }
      } finally {
        loop();
      }
    }

    // Call onFrame() on next animation frame
    function loop() {
      timeoutId = setTimeout(onFrame, delay);
    }

    // Start the loop
    const start = Date.now();
    loop();

    // Clean things up
    return () => {
      isCancelled = true;
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [delay]);
}

export function useLoop(callback: () => Promise<void>, delay = 0): void {
  const savedCallback = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let isCancelled = false;
    // Function to be executed on each animation frame
    async function onFrame() {
      if (isCancelled) return;
      try {
        const callback = savedCallback.current;
        if (callback != null) {
          await callback();
        }
      } finally {
        timeoutId = setTimeout(onFrame, delay);
      }
    }

    // Start the loop
    onFrame();

    // Clean things up
    return () => {
      isCancelled = true;
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, [delay]);
}

export function useMeasure(options: MeasureOptions = {}): ReturnType<typeof originalUseMeasure> {
  return originalUseMeasure({ ...options, polyfill: ResizeObserver });
}
